import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';

import clsx from 'clsx';
import {
  Box,
  CircularProgress,
  IconButton,
  TextareaAutosize,
  Tooltip,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FiArrowUp, FiCheck, FiMic, FiPaperclip, FiSquare, FiTrash2 } from 'react-icons/fi';

import { DeviceContext } from '../../../contexts/deviceContext';
import { NotificationContext } from '../../../contexts/notificationContext';
import { chatBubbleColors } from './ChatBubble';
import { useVoiceRecorder, voiceSupported } from './useVoiceRecorder';

const formatSeconds = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export interface ChatComposerRef {
  focus: () => void;
}

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  // Called with the text as typed; the composer doesn't clear itself.
  onSend: (text: string) => void;
  // Lets a message go without text, e.g. when files are attached.
  canSendEmpty?: boolean;
  placeholder?: string;
  // Saving an edit rather than sending a new message.
  editing?: boolean;
  // The other side is answering: the button becomes Stop (Laila).
  busy?: boolean;
  onStop?: () => void;
  stopLabel?: string;
  // The message is on its way: a spinner, and no second send.
  sending?: boolean;
  disabled?: boolean;
  // Esc while not busy: drop the reply or edit.
  onCancel?: () => void;
  // Attach button; files are handed over as picked.
  onAttach?: (files: File[]) => void;
  accept?: string;
  // Over the input: the reply/edit bar, attached files, suggestions.
  top?: React.ReactNode;
  // Buttons before the send button, e.g. Translate.
  extraActions?: React.ReactNode;
  // Dictation: the recording is transcribed into the box, to be checked
  // before sending. No microphone button without it.
  onTranscribe?: (recording: Blob) => Promise<string | null | undefined>;
  // Overrides for the notices, so each chat can speak in its own voice.
  micDeniedMessage?: string;
  unclearRecordingMessage?: string;
  autoFocus?: boolean;
}

// The message box shared by every chat: autosizing text, attachments,
// dictation, and one round button that is Send, Save, Stop or the microphone
// depending on what is going on.
export const ChatComposer = forwardRef<ChatComposerRef, ChatComposerProps>(
  (
    {
      value,
      onChange,
      onSend,
      canSendEmpty = false,
      placeholder = 'Message',
      editing = false,
      busy = false,
      onStop,
      stopLabel = 'Stop',
      sending = false,
      disabled = false,
      onCancel,
      onAttach,
      accept,
      top,
      extraActions,
      onTranscribe,
      micDeniedMessage = 'Allow microphone access in your browser settings to record a voice message.',
      unclearRecordingMessage = "Couldn't make out that recording.",
      autoFocus = true,
    },
    ref
  ) => {
    const classes = useStyles();
    const { isMobile } = useContext(DeviceContext);
    const { showNotification } = useContext(NotificationContext);
    const voice = useVoiceRecorder();
    const canRecord = useMemo(() => !!onTranscribe && voiceSupported(), [onTranscribe]);
    const [transcribing, setTranscribing] = useState(false);
    const input = useRef<HTMLTextAreaElement>(null);
    const filePicker = useRef<HTMLInputElement>(null);

    const focus = () => {
      const el = input.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    };
    useImperativeHandle(ref, () => ({ focus }));

    // Editing puts the old text in the box; the caret goes to its end.
    useEffect(() => {
      if (editing) {
        setTimeout(focus);
      }
    }, [editing]);

    const hasText = !!value.trim();
    const canSend = !disabled && !sending && !busy && !transcribing && (hasText || canSendEmpty);

    const submit = () => {
      if (canSend) {
        onSend(value);
        input.current?.focus();
      }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // On phones Enter adds a line, as in Telegram; the send button sends.
      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && !isMobile) {
        event.preventDefault();
        submit();
      } else if (event.key === 'Escape' && busy && onStop) {
        // Handled here, so Esc doesn't also close a panel around the composer.
        event.preventDefault();
        onStop();
      } else if (event.key === 'Escape' && onCancel) {
        event.preventDefault();
        onCancel();
      }
    };

    const attach = (picked: FileList | null) => {
      const files = Array.from(picked || []);
      if (filePicker.current) {
        filePicker.current.value = '';
      }
      if (files.length) {
        onAttach?.(files);
      }
    };

    const startRecording = async () => {
      try {
        await voice.start();
      } catch (e) {
        showNotification({ message: micDeniedMessage, options: { variant: 'warning' } });
      }
    };

    const finishRecording = async () => {
      const blob = await voice.stop();
      if (!blob || !onTranscribe) {
        return;
      }
      setTranscribing(true);
      const text = await onTranscribe(blob);
      setTranscribing(false);
      if (text) {
        onChange(value ? `${value} ${text}` : text);
        input.current?.focus();
      } else {
        showNotification({ message: unclearRecordingMessage, options: { variant: 'warning' } });
      }
    };

    const action =
      busy && onStop ? (
        <Tooltip title={`${stopLabel} (Esc)`}>
          <IconButton className={classes.action} onClick={onStop} aria-label={stopLabel}>
            <FiSquare size={16} className={classes.stopIcon} />
          </IconButton>
        </Tooltip>
      ) : transcribing || sending ? (
        <Box
          className={classes.action}
          role="progressbar"
          aria-label={transcribing ? 'Transcribing' : 'Sending'}
        >
          <CircularProgress size={20} color="inherit" />
        </Box>
      ) : editing ? (
        <IconButton className={classes.action} onClick={submit} disabled={!canSend} aria-label="Save">
          <FiCheck size={22} />
        </IconButton>
      ) : hasText || canSendEmpty || !canRecord ? (
        <IconButton className={classes.action} onClick={submit} disabled={!canSend} aria-label="Send">
          <FiArrowUp size={22} />
        </IconButton>
      ) : (
        <IconButton
          className={clsx(classes.action, classes.quiet)}
          onClick={startRecording}
          disabled={disabled}
          aria-label="Record a voice message"
        >
          <FiMic size={22} />
        </IconButton>
      );

    return (
      <Box className={clsx(classes.root, { [classes.disabled]: disabled })}>
        {top}

        {voice.recording ? (
          <Box className={classes.row}>
            <IconButton className={classes.quiet} onClick={voice.cancel} aria-label="Discard the recording">
              <FiTrash2 size={20} />
            </IconButton>
            <Box className={classes.recording} aria-live="polite">
              <span className={classes.recordingDot} />
              <Typography variant="body2">{formatSeconds(voice.seconds)}</Typography>
              <Box className={classes.meter} aria-hidden>
                {[0.5, 0.8, 1, 0.8, 0.5].map((weight, index) => (
                  <span
                    key={index}
                    className={classes.meterBar}
                    style={{ transform: `scaleY(${0.15 + Math.min(1, voice.level * weight * 1.6) * 0.85})` }}
                  />
                ))}
              </Box>
            </Box>
            <IconButton
              className={classes.action}
              onClick={finishRecording}
              aria-label="Stop recording and transcribe"
            >
              <FiArrowUp size={22} />
            </IconButton>
          </Box>
        ) : (
          <Box className={classes.row}>
            {onAttach && (
              <>
                <IconButton
                  className={classes.quiet}
                  onClick={() => filePicker.current?.click()}
                  disabled={disabled}
                  aria-label="Attach a file"
                >
                  <FiPaperclip size={20} />
                </IconButton>
                <input
                  ref={filePicker}
                  type="file"
                  multiple
                  hidden
                  accept={accept}
                  onChange={event => attach(event.target.files)}
                />
              </>
            )}
            <TextareaAutosize
              ref={input}
              value={value}
              onChange={event => onChange(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={transcribing ? 'Transcribing…' : placeholder}
              disabled={disabled || transcribing}
              autoFocus={autoFocus && !isMobile}
              minRows={1}
              maxRows={8}
              aria-label={placeholder}
              className={clsx(classes.input, { [classes.inputAlone]: !onAttach })}
            />
            {extraActions}
            {action}
          </Box>
        )}
      </Box>
    );
  }
);

const useStyles = makeStyles((theme: Theme) => {
  const { outBackground } = chatBubbleColors(theme);

  return createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      padding: 6,
      borderRadius: 24,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      transition: 'border-color 150ms',
      '&:focus-within': { borderColor: theme.palette.primary.main },
    },
    disabled: { opacity: 0.6 },
    row: { display: 'flex', alignItems: 'flex-end', gap: 4 },
    input: {
      flex: 1,
      minWidth: 0,
      resize: 'none',
      border: 0,
      outline: 'none',
      background: 'transparent',
      color: theme.palette.text.primary,
      font: 'inherit',
      fontSize: 15,
      lineHeight: '22px',
      padding: '9px 4px',
      '&::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
    },
    inputAlone: { paddingLeft: 12 },
    action: {
      flex: 'none',
      width: 40,
      height: 40,
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      backgroundColor: outBackground,
      color: theme.palette.common.white,
      '&:hover': { backgroundColor: theme.palette.primary.dark },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
      },
    },
    quiet: {
      flex: 'none',
      width: 40,
      height: 40,
      padding: 0,
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      '&:hover': { backgroundColor: theme.palette.action.hover, color: theme.palette.primary.main },
    },
    stopIcon: { fill: 'currentColor' },
    recording: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      height: 40,
      padding: '0 8px',
    },
    recordingDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: theme.palette.error.main,
      animation: '$pulse 1.2s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    meter: { display: 'flex', alignItems: 'center', gap: 3, height: 24 },
    meterBar: {
      width: 4,
      height: 24,
      borderRadius: 2,
      backgroundColor: theme.palette.primary.main,
      transition: 'transform 80ms linear',
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.3 },
    },
  });
});
